import {
  ARRANJOS_FRONTAIS,
  CLASSES_VEICULO,
  CLASSES_VEICULO_ORDEM,
  PONTOS_CONTATO_LATERAL,
  VELOCIDADES_OUTRO_VEICULO,
  arranjoDoEvento,
  type ConfiguracaoOutroVeiculo,
} from '../../dominio/didatico/colisaoEntreVeiculos'
import type { TipoColisao } from '../../dominio/didatico/timelineCinematica'

interface Props {
  tipoColisao: TipoColisao
  config: ConfiguracaoOutroVeiculo
  aoAlterar: (parcial: Partial<ConfiguracaoOutroVeiculo>) => void
  /** leitura da colisão resolvida (velocidade relativa, ΔV) */
  leitura?: string | null
}

/**
 * Configuração do OUTRO veículo quando o alvo é um veículo.
 *
 * Só aparece o que muda a cena naquele tipo: arranjo e velocidade do outro só
 * fazem sentido quando é o veículo da maquete que se move de frente; o ponto
 * de contato lateral só quando alguém recebe o impacto na lateral.
 */
export function SeletorOutroVeiculo({ tipoColisao, config, aoAlterar, leitura }: Props) {
  const arranjo = arranjoDoEvento(tipoColisao, config)
  if (!arranjo) return null
  const raspagem = arranjo === 'raspagemLateral'
  const aMove = arranjo === 'frenteAFrente' || arranjo === 'frenteContraLateral' || arranjo === 'frenteContraTraseira'
  const contatoLateral = arranjo === 'frenteContraLateral' || arranjo === 'lateralRecebida'
  const alinhamento = arranjo === 'frenteAFrente' || arranjo === 'frenteContraTraseira'

  return (
    <div className="grupo-controle" data-testid="seletor-outro-veiculo">
      <span className="titulo-controle">Outro veículo (B)</span>

      <div className="grade-alvos-impacto" role="group" aria-label="Classe do outro veículo">
        {CLASSES_VEICULO_ORDEM.map((classe) => (
          <button
            key={classe}
            type="button"
            className={(config.classe ?? null) === classe ? 'ativo' : ''}
            onClick={() => aoAlterar({ classe: config.classe === classe ? null : classe })}
            title={`${CLASSES_VEICULO[classe].massaKg} kg (aprox.)`}
          >
            {CLASSES_VEICULO[classe].nome}
          </button>
        ))}
      </div>

      {aMove && (
        <>
          <div className="grade-alvos-impacto" role="group" aria-label="Arranjo da colisão">
            {ARRANJOS_FRONTAIS.map((opcao) => (
              <button
                key={opcao.id}
                type="button"
                className={config.arranjoFrontal === opcao.id ? 'ativo' : ''}
                onClick={() => aoAlterar({ arranjoFrontal: opcao.id })}
              >
                {opcao.nome}
              </button>
            ))}
          </div>
          <p className="dica">{ARRANJOS_FRONTAIS.find((o) => o.id === config.arranjoFrontal)?.descricao}</p>
        </>
      )}

      {raspagem && (
        <>
          <div className="grade-alvos-impacto" role="group" aria-label="Sentido do outro veículo">
            {(['mesmoSentido', 'sentidoOposto'] as const).map((sentido) => (
              <button
                key={sentido}
                type="button"
                className={config.sentidoRaspagem === sentido ? 'ativo' : ''}
                onClick={() => aoAlterar({ sentidoRaspagem: sentido })}
              >
                {sentido === 'mesmoSentido' ? 'Mesmo sentido' : 'Sentido oposto'}
              </button>
            ))}
          </div>
          <p className="dica">
            {config.sentidoRaspagem === 'mesmoSentido'
              ? 'Troca de faixa: a diferença de velocidades é pequena, a raspagem é curta.'
              : 'Sentidos opostos: as velocidades se somam na tangente — riscos longos, amassado ainda raso.'}
          </p>
        </>
      )}

      {(aMove || raspagem) && (
        <div className="grade-alvos-impacto" role="group" aria-label="Velocidade do outro veículo">
          {VELOCIDADES_OUTRO_VEICULO.filter((kmh) => !raspagem || kmh > 0).map((kmh) => (
            <button
              key={kmh}
              type="button"
              className={config.velocidadeKmh === kmh ? 'ativo' : ''}
              onClick={() => aoAlterar({ velocidadeKmh: kmh })}
            >
              {kmh === 0 ? 'parado' : `${kmh} km/h`}
            </button>
          ))}
        </div>
      )}

      {alinhamento && (
        <div className="grade-alvos-impacto" role="group" aria-label="Alinhamento do contato">
          {(['central', 'deslocado'] as const).map((opcao) => (
            <button
              key={opcao}
              type="button"
              className={config.alinhamento === opcao ? 'ativo' : ''}
              onClick={() => aoAlterar({ alinhamento: opcao })}
            >
              {opcao === 'central' ? 'Contato central' : 'Deslocado (40%)'}
            </button>
          ))}
        </div>
      )}

      {contatoLateral && (
        <div className="grade-alvos-impacto" role="group" aria-label="Ponto de contato na lateral">
          {PONTOS_CONTATO_LATERAL.map((ponto) => (
            <button
              key={ponto.id}
              type="button"
              className={config.pontoLateral === ponto.id ? 'ativo' : ''}
              onClick={() => aoAlterar({ pontoLateral: ponto.id })}
            >
              {ponto.nome}
            </button>
          ))}
        </div>
      )}

      {leitura && <p className="dica">{leitura}</p>}
    </div>
  )
}
